import { isEmpty, isEqual } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { default as ComboMultiSelectAsync } from 'app/components/ui/combo/combo-multi-select-async'
import { FilterObject, FilterValueType, FilterableColumn } from 'app/filter.utils'
import { SearchResponse } from 'app/services/search'
import { OptionType } from 'types/OptionType'

export function AsyncMultiSelectFilter({
  column,
  contextualValue,
  filter,
  openFilterOptions = false,
  onValueChange,
}: {
  column: FilterableColumn
  contextualValue?: string | string[]
  filter: FilterObject
  openFilterOptions: boolean
  onValueChange: (val: string[]) => void
}): ReactElement {
  const [value, setValue] = useState<string[] | undefined>(
    filter.value ? (Array.isArray(filter.value) ? filter.value : [filter.value]) : undefined,
  )
  const [comboMultiSelectAsyncValue, setComboMultiSelectAsyncValue] = useState<string[] | undefined>(() => {
    if (value !== undefined) return Array.isArray(value) ? value : [value]
    return undefined
  })
  const [dependantValue, setDependantValue] = useState<string | string[] | undefined>(value)
  const [additionalValues, setAdditionalValues] = useState<Record<string, string> | undefined>(column.additionalParams)
  const [enableDeselectAllOption, setEnableDeselectAllOption] = useState<boolean>(value !== undefined)
  const handleDataFound = () => {
    /**
     * It selects All option if query returns values, it only runs once.
     *
     * FIX: Removed `onValueChange([])` which was emitting an empty filter on mount when `fetcher`
     * returned data eagerly. This caused an ES parse error: `owner.name in ()`.
     * Previously with REST `queryURL`, data was only fetched on dropdown open (user interaction),
     * so this path never fired on mount.
     *
     * Affected filters using `fetcher` + `FilterValueType.id`:
     * - /activities — Staff filter (owner.name)
     * - /contacts — Contact Owner filter (leadOwner.name)
     * - /tasks — Owner filter (ownerId), Created By filter (createdById)
     *
     * TODO: Remove this comment after verifying all filters above work correctly.
     */
    if (enableDeselectAllOption === false && filter.value === undefined) {
      setEnableDeselectAllOption(true)
      setValue(filter.value ? (Array.isArray(filter.value) ? filter.value : [filter.value]) : undefined)
    }
  }

  useEffect(() => {
    if (!isEmpty(column.contextualFiltering) && contextualValue?.length) {
      let values = {
        ...(column.additionalParams ?? {}),
        [Object.values(column.contextualFiltering)[0]]: Array.isArray(contextualValue)
          ? `(${contextualValue.join(` OR `)})`
          : contextualValue,
      }
      if (!isEqual(additionalValues, values)) {
        setAdditionalValues(values)
        setDependantValue([])
        onValueChange([])
      }
    } else setAdditionalValues(column.additionalParams)
  }, [contextualValue])

  const optionsMapper = (response: SearchResponse<any>): OptionType[] => {
    return response.hits?.map((item) => ({ id: item.id!, value: item.id!, name: item.name! }))
  }
  return (
    <div className="mb-4">
      {filter.type === FilterValueType.id && (
        <ComboMultiSelectAsync
          placeholder="Select"
          defaultOpen={openFilterOptions}
          deselectAllOption={column.nonEmpty ? { name: `All`, id: `0`, value: `*` } : undefined}
          name="filterMulti"
          inputClassName="!h-8 cursor-pointer rounded-lg border border-default !px-2 !text-body-sm !pt-3"
          optionsContainerClassName="max-h-[480px]"
          values={dependantValue as string[]}
          queryURL={column.optionsSource}
          fetcher={column.fetcher}
          enableDeselectAllOption={enableDeselectAllOption}
          additionalParams={additionalValues}
          optionsMapper={(res) => {
            // if response has total, it means it's a search response
            // e.g. contact owners
            if (`total` in res) {
              if (res.total > 0) handleDataFound()
              setDependantValue((prev) => (res.total > 0 ? prev : undefined))
            }
            if (column.optionsMapper) {
              // getSelectedOptionName(res)
              return column.optionsMapper(res)
            }
            return optionsMapper(res)
          }}
          onChangeHandler={(options) => {
            onValueChange(
              options?.map((option) => {
                if (column.queryByName) return option.name === `All` ? `` : option.name
                return option.value!
              }),
            )
          }}
          withTooltip
          disabled={column.readOnly}
          isOptionsGrouped={column.groupOptions}
        />
      )}
      {filter.type === FilterValueType.stringArray && (
        <ComboMultiSelectAsync
          name="filterMulti"
          inputClassName="!h-8 cursor-pointer rounded-lg border border-default !px-2 !text-body-sm !pt-3"
          optionsContainerClassName="max-h-[480px]"
          values={comboMultiSelectAsyncValue}
          queryURL={column.optionsSource}
          fetcher={column.fetcher}
          enableDeselectAllOption={enableDeselectAllOption}
          additionalParams={column.additionalParams}
          optionsMapper={(res) => {
            // If optionsMapper is provided, use it to map the response, eg. Campaign on Dashboard
            if (column.optionsMapper) return column.optionsMapper(res)
            // if response has total, it means it's a search response
            // e.g. contact owners
            if (`total` in res) {
              setEnableDeselectAllOption(res.total > 0)
              setComboMultiSelectAsyncValue((prev) => (res.total > 0 ? prev : undefined))
              return res?.hits?.map?.((entry) => ({ id: entry.id, value: entry.id, name: entry.name }) as OptionType)
            }
            // if response does not have total, it means it's a list response
            // e.g. tags
            return res?.map?.((entry) => ({ id: entry, value: entry, name: entry }))
          }}
          onChangeHandler={(options) => {
            onValueChange(
              options?.map((option) => {
                if (column.queryByName) return option.name
                return option.value!
              }),
            )
          }}
          queryValues={false}
          disabled={column.readOnly}
        />
      )}
    </div>
  )
}
